import { useCallback, useEffect, useRef, useState } from 'react';

import { getEquieGestor } from 'services/dashboard.service';
import { EquipeResumo, getEquipeResumo } from 'services/minha-equipe.service';

import { POR_PAGINA_DA_EQUIPE } from './components/ListaDaEquipe';
import { GestorLateral } from './components/ModalEquipeLateral';
import { FunciDaEquipe } from './minha-equipe-calculos';

/**
 * Estado de dados da Minha Equipe: gestor visto (próprio ou lateral), página, busca e o resumo do ciclo.
 * O resumo é lido uma vez por gestor visto e reaproveitado ao paginar e buscar (EQP-20).
 */

export const ESPERA_DA_BUSCA_MS = 400;

/**
 * O `equipe_gestor` monta uma regex com o texto sem escapar; "(" ou "[" derrubam a consulta. Escapar não
 * serve: o servidor troca `- _ / . ,` por espaço e cerca cada palavra com `\b`, então "\." vira "\ " e
 * "\(" nunca casa depois de um espaço. Os caracteres especiais viram espaço, como o servidor já faz com
 * os dele: "J.R." busca "J" e "R", e "Silva (BB)" busca "Silva" e "BB".
 */
export function termoDeBusca(texto: string): string {
  return texto
    .replace(/[.*+?^${}()|[\]\\/]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

interface PaginaDaEquipe {
  funcis: FunciDaEquipe[];
  total: number;
}

const semOProprioGestor = (funcis: unknown, proprioId: string) =>
  (Array.isArray(funcis) ? (funcis as FunciDaEquipe[]) : []).filter(funci => funci._id !== proprioId);

export function useEquipe(proprioId: string | undefined) {
  const [equipeLateral, setEquipeLateral] = useState<GestorLateral | null>(null);
  const [busca, setBusca] = useState('');
  const [termo, setTermo] = useState('');
  const [pagina, setPagina] = useState(0);
  const [lista, setLista] = useState<PaginaDaEquipe>({ funcis: [], total: 0 });
  const [carregando, setCarregando] = useState(true);
  const [erroDaLista, setErroDaLista] = useState(false);
  /** Por gestor: `undefined` ainda não lido, `null` falhou. */
  const [resumos, setResumos] = useState<Record<string, EquipeResumo | null>>({});
  /** `range.total` da busca vazia, por gestor (panorama e exportação não mudam com a busca). */
  const [totaisSemBusca, setTotaisSemBusca] = useState<Record<string, number>>({});
  const [versao, setVersao] = useState(0);

  const ultimaLeitura = useRef(0);
  const contadorDeResumos = useRef(0);
  /** Pedido de resumo em voo ou concluído por gestor; a resposta de um pedido descartado não vale. */
  const resumosPedidos = useRef<Record<string, number>>({});
  const esperaDaBusca = useRef<ReturnType<typeof setTimeout>>();

  const gestorVisto = equipeLateral?.id ?? proprioId;

  useEffect(() => {
    if (!gestorVisto || !proprioId) return;
    const leitura = ++ultimaLeitura.current;

    setCarregando(true);
    setErroDaLista(false);
    getEquieGestor(gestorVisto, termo, pagina * POR_PAGINA_DA_EQUIPE, POR_PAGINA_DA_EQUIPE)
      .then(({ result, range }) => {
        // Só a leitura mais recente atualiza a lista (respostas fora de ordem são descartadas).
        if (leitura !== ultimaLeitura.current) return;
        const total = Number.isFinite(range.total) ? range.total : 0;

        // Página além do total depois de uma busca: volta à última página válida.
        const ultimaPagina = Math.max(0, Math.ceil(total / POR_PAGINA_DA_EQUIPE) - 1);
        if (pagina > ultimaPagina) {
          setPagina(ultimaPagina);
          return;
        }

        setLista({ funcis: semOProprioGestor(result, proprioId), total });
        if (!termo) setTotaisSemBusca(atuais => ({ ...atuais, [gestorVisto]: total }));
        setCarregando(false);
      })
      .catch(() => {
        if (leitura !== ultimaLeitura.current) return;
        setLista({ funcis: [], total: 0 });
        setErroDaLista(true);
        setCarregando(false);
      });
  }, [gestorVisto, proprioId, termo, pagina, versao]);

  useEffect(() => {
    if (!gestorVisto || resumosPedidos.current[gestorVisto]) return;
    const pedido = ++contadorDeResumos.current;
    resumosPedidos.current[gestorVisto] = pedido;

    getEquipeResumo(gestorVisto).then(resumo => {
      if (resumosPedidos.current[gestorVisto] !== pedido) return;
      setResumos(atuais => ({ ...atuais, [gestorVisto]: resumo }));
    });
  }, [gestorVisto, versao]);

  useEffect(() => () => clearTimeout(esperaDaBusca.current), []);

  const aplicarTermo = useCallback((texto: string) => {
    clearTimeout(esperaDaBusca.current);
    setTermo(termoDeBusca(texto));
    setPagina(0);
  }, []);

  /** Digitação: busca 400 ms depois da última tecla (EQP-14). */
  const digitarBusca = useCallback(
    (texto: string) => {
      setBusca(texto);
      clearTimeout(esperaDaBusca.current);
      esperaDaBusca.current = setTimeout(() => aplicarTermo(texto), ESPERA_DA_BUSCA_MS);
    },
    [aplicarTermo],
  );

  function trocarDeEquipe(gestor: GestorLateral | null) {
    clearTimeout(esperaDaBusca.current);
    setEquipeLateral(gestor);
    setBusca('');
    setTermo('');
    setPagina(0);
  }

  /** Relê a lista e o resumo do gestor visto (depois de adicionar ou trocar gestor, ou em "Tentar de novo"). */
  function recarregar() {
    if (gestorVisto) {
      delete resumosPedidos.current[gestorVisto];
      setResumos(atuais => {
        const restantes = { ...atuais };
        delete restantes[gestorVisto];
        return restantes;
      });
    }
    setVersao(atual => atual + 1);
  }

  /** Equipe inteira da visão atual (todas as páginas, sem busca), para a exportação (EQP-50). */
  async function lerEquipeInteira(): Promise<FunciDaEquipe[]> {
    if (!gestorVisto || !proprioId) return [];
    const total = totaisSemBusca[gestorVisto] ?? lista.total;
    const { result } = await getEquieGestor(gestorVisto, '', 0, total);
    return semOProprioGestor(result, proprioId);
  }

  const resumoVisto = gestorVisto ? resumos[gestorVisto] : undefined;

  return {
    equipeLateral,
    verEquipeLateral: (gestor: GestorLateral) => trocarDeEquipe(gestor),
    verMinhaEquipe: () => trocarDeEquipe(null),
    busca,
    digitarBusca,
    /** Enter: busca sem esperar. */
    enviarBusca: aplicarTermo,
    pagina,
    irParaPagina: setPagina,
    funcis: lista.funcis,
    total: lista.total,
    carregando,
    erroDaLista,
    /** Resumo do gestor visto; null enquanto carrega ou quando falha. */
    resumo: resumoVisto ?? null,
    carregandoResumo: resumoVisto === undefined,
    erroDoResumo: resumoVisto === null,
    /** Panorama: sempre a própria equipe, sem busca. */
    resumoDaMinhaEquipe: (proprioId && resumos[proprioId]) || null,
    totalDaMinhaEquipe: proprioId ? totaisSemBusca[proprioId] ?? null : null,
    recarregar,
    lerEquipeInteira,
  };
}
